import React, { createContext, useContext, useMemo } from "react";
import { useQueries } from "react-query";
import { resolveVersion } from "./dataVersion";

interface WikiDataContextValue {
  version: string;
}

const WikiDataContext = createContext<WikiDataContextValue | null>(null);

export const useDataVersion = (): string => {
  const ctx = useContext(WikiDataContext);
  if (!ctx) throw new Error("useDataVersion must be used within WikiDataProvider");
  return ctx.version;
};

export const WikiDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const version = useMemo(() => resolveVersion(), []);
  return (
    <WikiDataContext.Provider value={{ version }}>
      {children}
    </WikiDataContext.Provider>
  );
};

function getBasePath(version: string): string {
  return `${__DATA_BASE__}/data/${version}/${__SITE_SLUG__}`;
}

export interface WikiDataResult {
  data: Record<string, any> | null;
  isLoading: boolean;
  error: Error | null;
}

export function useWikiData(keys: string[]): WikiDataResult {
  const version = useDataVersion();
  const basePath = useMemo(() => getBasePath(version), [version]);

  const queries = useQueries(
    keys.map((key) => ({
      queryKey: ["wiki-data", version, key],
      queryFn: () =>
        fetch(`${basePath}/${key}.json`).then((res) => {
          if (!res.ok) throw new Error(`Failed to load ${key}.json (${res.status})`);
          return res.json();
        }),
      staleTime: Infinity,
      cacheTime: Infinity,
      retry: 0,
    })),
  );

  const isLoading = queries.some((q) => q.isLoading);
  const errors = queries.filter((q) => q.error).map((q) => q.error as Error);
  const allLoaded = queries.every((q) => q.isSuccess);

  const data = useMemo(() => {
    if (!allLoaded) return null;
    const map: Record<string, any> = {};
    keys.forEach((key, i) => {
      map[key] = queries[i].data;
    });
    return map;
  }, [allLoaded, keys, queries]);

  return {
    data,
    isLoading,
    error: errors.length > 0 ? errors[0] : null,
  };
}
